import { ColumnDef } from "@tanstack/react-table";
import { ClockIcon } from "assets/icons";
import cn from "classnames";
import { If } from "components";
import { useLanguage } from "hooks";
import { t } from "i18next";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ItemsTable, Icon, Status, TableItemStatus } from "ui";

import {
  EARNING_RESELLER_FILTER_SEARCH_PARAMETER,
  EARNING_SEARCH_SEARCH_PARAMETER,
  EARNING_TYPE_FILTER_SEARCH_PARAMETER,
  EarningChapters,
  EarningType
} from "modules/financial";
import { ORDER_STATUSES_LIST } from "modules/orders";

import {
  RESELLER_COMMISSION_STATUSES_LIST,
  ResellerCommissionsOrderBy,
  ResellerCommissionStatus
} from "../../constants";
import { formatAmountInFcfa } from "../../helpers";
import { ResellerCommission } from "../../resellers.types";
import { CommissionSourceCell } from "./atoms";
import { ResellerCommissionsTableProps } from "./ResellerCommissionsTable.types";

import styles from "./ResellerCommissionsTable.module.scss";

const ResellerCommissionsTable = ({
  commissions,
  isLoading,
  sorting,
  setSorting,
  shownTotal,
  shownCount,
  resellerId,
  onRowClick
}: ResellerCommissionsTableProps) => {
  const { currentLanguage } = useLanguage();

  /**
   * The payout actions live in the Financial section, under their own privilege.
   * The link lands on the chapter the earning actually sits in, pre-filtered to
   * this influencer's referral commissions and searched by order id.
   */
  const getPayoutLink = (commission: ResellerCommission) => {
    const chapter =
      commission.status === ResellerCommissionStatus.Paid
        ? EarningChapters.Paid
        : commission.status === ResellerCommissionStatus.Void
          ? EarningChapters.Canceled
          : commission.canBePaidNoEarlierThan &&
              moment(commission.canBePaidNoEarlierThan).isSameOrBefore(moment())
            ? EarningChapters.AvailableToBePaid
            : EarningChapters.Pending;

    const params = new URLSearchParams({
      [EARNING_RESELLER_FILTER_SEARCH_PARAMETER]: resellerId ?? "",
      [EARNING_TYPE_FILTER_SEARCH_PARAMETER]:
        EarningType.ProductsResellingByReferral,
      [EARNING_SEARCH_SEARCH_PARAMETER]: commission.orderId
    });

    return `/earnings/${chapter}?${params.toString()}`;
  };

  const statusesList: Status[] = RESELLER_COMMISSION_STATUSES_LIST.map(
    (status) => ({ ...status, onClick: () => {} })
  );

  const orderStatusesList: Status[] = ORDER_STATUSES_LIST.map((status) => ({
    ...status,
    onClick: () => {}
  }));

  const columns: Array<ColumnDef<ResellerCommission, ResellerCommission[]>> =
    useMemo(
      () => [
        {
          id: ResellerCommissionsOrderBy.Date,
          accessorKey: "orderCreatedAt",
          header: t("Order"),
          size: 16,
          minSize: 16,
          cell: ({ row }) => (
            <div className={styles.orderCell}>
              <span className={styles.orderCell_id}>{row.original.orderId}</span>
              <span className={styles.orderCell_date}>
                {moment(row.original.orderCreatedAt).format("DD/MM/YYYY HH:mm")}
              </span>

              {/* A cancelled or failed order is exactly why a row can show a
                  zero commission, so the order status belongs on the line. */}
              <TableItemStatus
                className={styles.orderCell_status}
                activeStatusIndex={orderStatusesList.findIndex(
                  (status) => status.value === row.original.orderStatus
                )}
                statusesList={orderStatusesList}
              />
            </div>
          )
        },
        {
          id: "buyer",
          header: t("Customer"),
          size: 18,
          minSize: 18,
          cell: ({ row }) => (
            <div className={styles.buyerCell}>
              <div className={styles.buyerCell_name}>
                {`${row.original.buyer.firstname} ${row.original.buyer.lastname}`}
              </div>
              <div className={styles.buyerCell_phone}>
                {row.original.buyer.phoneNumber}
              </div>
            </div>
          )
        },
        {
          id: ResellerCommissionsOrderBy.OrderAmount,
          accessorKey: "orderAmount",
          header: `${t("Order Amount")} (FCFA)`,
          size: 14,
          minSize: 14,
          cell: ({ row }) => row.original.orderAmount.toLocaleString("en-US")
        },
        {
          id: "source",
          header: t("Commission Source"),
          size: 22,
          minSize: 22,
          cell: ({ row }) => <CommissionSourceCell commission={row.original} />
        },
        {
          id: ResellerCommissionsOrderBy.CommissionAmount,
          accessorKey: "commissionAmount",
          header: `${t("Commission")} (FCFA)`,
          size: 14,
          minSize: 14,
          cell: ({ row }) => {
            const isVoid =
              row.original.status === ResellerCommissionStatus.Void;

            return (
              <div className={styles.commissionCell}>
                <span
                  className={cn(styles.commissionCell_amount, {
                    [styles.commissionCell_amount__void]: isVoid
                  })}
                >
                  {row.original.commissionAmount.toLocaleString("en-US")}
                </span>

                <If condition={!isVoid && !!row.original.commissionRatePct}>
                  <span className={styles.commissionCell_rate}>
                    {`${Math.round(Number(row.original.commissionRatePct))}% ${t("of the order")}`}
                  </span>
                </If>
              </div>
            );
          }
        },
        {
          id: "status",
          header: t("Status"),
          size: 16,
          minSize: 16,
          cell: ({ row }) => {
            const activeStatusIndex = statusesList.findIndex(
              (status) => status.value === row.original.status
            );

            return (
              <div className={styles.statusCell}>
                <TableItemStatus
                  activeStatusIndex={activeStatusIndex}
                  statusesList={statusesList}
                />

                {/* No ledger row yet: the order has not been delivered, so
                    there is nothing to pay out on this line. */}
                <If
                  condition={
                    !row.original.earningId &&
                    row.original.status === ResellerCommissionStatus.Pending
                  }
                >
                  <span className={styles.statusCell_note}>
                    <Icon as={ClockIcon} width={12} height={12} />
                    {t("Awaiting delivery")}
                  </span>
                </If>

                <If condition={!!row.original.paidAt}>
                  <span className={styles.statusCell_note}>
                    {moment(row.original.paidAt).format("DD/MM/YYYY")}
                  </span>
                </If>

                <If condition={!!row.original.earningId}>
                  <Link
                    className={styles.statusCell_link}
                    to={getPayoutLink(row.original)}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {t("View payout")}
                  </Link>
                </If>

                <If
                  condition={
                    !row.original.paidAt &&
                    !!row.original.canBePaidNoEarlierThan
                  }
                >
                  <span className={styles.statusCell_note}>
                    {`${t("Payable from")} ${moment(row.original.canBePaidNoEarlierThan).format("DD/MM/YYYY")}`}
                  </span>
                </If>
              </div>
            );
          }
        }
      ],
      [commissions, currentLanguage]
    );

  return (
    <>
      <ItemsTable
        className={styles.table}
        columns={columns}
        data={commissions}
        sorting={sorting}
        setSorting={setSorting}
        isLoading={isLoading}
        skeletonHeight={65}
        onRowClick={onRowClick}
        emptyListMessage="No commission has been generated yet"
      />

      <If condition={commissions.length > 0 && shownTotal !== undefined}>
        <div className={styles.footer}>
          <span className={styles.footer_count}>
            {`${shownCount ?? commissions.length} ${t("orders shown")}`}
          </span>

          <span className={styles.footer_label}>{t("Total shown")}</span>
          <span className={styles.footer_value}>
            {formatAmountInFcfa(shownTotal)}
          </span>
        </div>
      </If>
    </>
  );
};

export default ResellerCommissionsTable;
